/**
 * Where each instrument sits, how its sound ends, and how its tone changes with how hard it
 * is played -- the things about an instrument a recording of one note does not say.
 *
 * Seats follow the usual concert layout seen from the hall: violins left, violas and cellos
 * right of centre, basses far right, the woodwinds in the middle, horns left and the heavy
 * brass right; saxophones, which that layout has no chairs for, across the woodwinds' middle
 * from the highest to the lowest, a guitar in front, where a soloist sits, a harp on the
 * left behind the violins with a celesta between it and the piano, where the keyboards
 * stand, and an organ in the middle, where its pipes are. By General MIDI program, since that is all a note brings with it,
 * so two violin sections share a seat. They are kept well inside the edges: a part hard in
 * one ear is a headphone effect, not a stage.
 */

import type { Seat } from "./sampler-room";

export interface Instrument {
  readonly seat: Seat;
  /**
   * Seconds for the sound to fall to a third once the note is let go: a bow lifting, a
   * breath stopping, a damper falling. Short, since a player ends a note on purpose, but not
   * zero -- that is what made every note end as if it were cut off with scissors.
   */
  readonly letGo: number;
  /** Whether it holds a tone for as long as it is played, and so may be looped. */
  readonly sustained: boolean;
}

const STRING = 0.09;
/** A plucked string is not stopped where its note ends: it rings until it has died away. */
const PLUCKED = 0.25;
const WIND = 0.06;
const BRASS = 0.07;

const INSTRUMENTS: ReadonlyMap<number, Instrument> = new Map([
  [0, { seat: -0.15, letGo: 0.14, sustained: false }],
  [8, { seat: -0.25, letGo: 0.14, sustained: false }],
  [19, { seat: 0, letGo: WIND, sustained: true }],
  [24, { seat: -0.05, letGo: 0.14, sustained: false }],
  [40, { seat: -0.45, letGo: STRING, sustained: true }],
  [41, { seat: 0.2, letGo: STRING, sustained: true }],
  [42, { seat: 0.4, letGo: STRING, sustained: true }],
  [43, { seat: 0.55, letGo: STRING, sustained: true }],
  [46, { seat: -0.35, letGo: PLUCKED, sustained: false }],
  [56, { seat: 0.2, letGo: BRASS, sustained: true }],
  [57, { seat: 0.35, letGo: BRASS, sustained: true }],
  [58, { seat: 0.45, letGo: BRASS, sustained: true }],
  [60, { seat: -0.3, letGo: BRASS, sustained: true }],
  [64, { seat: -0.12, letGo: WIND, sustained: true }],
  [65, { seat: -0.04, letGo: WIND, sustained: true }],
  [66, { seat: 0.04, letGo: WIND, sustained: true }],
  [67, { seat: 0.12, letGo: WIND, sustained: true }],
  [68, { seat: 0.1, letGo: WIND, sustained: true }],
  [69, { seat: 0.15, letGo: WIND, sustained: true }],
  [70, { seat: 0.1, letGo: WIND, sustained: true }],
  [71, { seat: -0.1, letGo: WIND, sustained: true }],
  [72, { seat: -0.15, letGo: WIND, sustained: true }],
  [73, { seat: -0.1, letGo: WIND, sustained: true }],
]);

/** An instrument not listed: in the middle, ending as a wind does, played once through. */
const UNSEATED: Instrument = { seat: 0, letGo: WIND, sustained: false };

export function instrumentOf(program: number): Instrument {
  return INSTRUMENTS.get(program) ?? UNSEATED;
}

/** The same instrument in the same seat with its bow put down: plucked, and left to ring. */
export function pluckedOf(instrument: Instrument): Instrument {
  return { ...instrument, letGo: PLUCKED, sustained: false };
}

/** Multiples of `letGo` a sound is kept for after it is let go: to -52 dB. */
export const TAIL_SPANS = 6;

/**
 * The corner, in hertz, of the low-pass a note of `hz` struck at `velocity` is played
 * through.
 *
 * Played softly, an instrument has fewer and weaker upper partials, not only less of
 * everything, and a recording of one loudness turned down is a loud note heard from far
 * away. So the corner is set by how hard the note is played: counted in the note's own
 * harmonics -- about seven at *pppp*, sixty at *ffff* -- so a low note is not made duller
 * than a high one at the same mark, and never below a floor that also rises with velocity,
 * so a bass line keeps its edge. Loud notes are left all but open.
 */
export function brightness(hz: number, velocity: number, nyquist: number): number {
  const v = Math.min(1, Math.max(0, velocity));
  const harmonics = 6 * 10 ** v;
  const floor = 1000 * 10 ** v;
  return Math.min(nyquist * 0.9, Math.max(hz * harmonics, floor));
}
